import React, {useEffect, useState} from 'react';
import {Box, Stack} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {Outlet, useNavigate} from 'react-router-dom';
import {useEffectFn} from '@ngneat/effects-hooks';
import {useObservable} from '@ngneat/react-rxjs';
import {emitOnce} from '@ngneat/elf';
import {GridCellParams, GridRowParams, GridValueGetterParams} from '@mui/x-data-grid-pro';
import {useSnackbar} from 'notistack';

import {
  ClientsService,
  clientsServicesQuery,
  clientsServicesService,
  ServiceValidationStatusEnum
} from '@store/clients/services';
import {searchClientsServicesEffect} from '@store/clients/services/clients-services.effects';

import SearchComponent from '@components/inputs/search/Search.component';
import TableFiltersComponent from '@components/table/filters/TableFilters.component';
import NewTableComponent from '@components/table/NewTable.component';

import COLUMN_TYPES from '@utils/table/columnTypes.table';
import {ConfirmActionProps, OtherActionType} from '@utils/table/columnTypes/actions.columnType';
import ConfirmModal from '@components/modal/Confirm.modal';
import {finalize} from 'rxjs';
import {BlackCheck, GreyCheck} from '@icons/index';
import CheckboxComponent from '@components/inputs/checkbox/Checkbox.component';
import SelectComponent from '@components/inputs/select/Select.component';
import dayjs from '@utils/dayjs.utils';

interface ClientsServicesTableProps {
  enableActions?: boolean;
  showRowsOnly?: boolean;
  contractIds?: number[];
}

const ClientsServicesTable = (props: ClientsServicesTableProps) => {
  const {enableActions = true, showRowsOnly = false, contractIds} = props;
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {enqueueSnackbar} = useSnackbar();

  const [openConfirmAction, setOpenConfirmAction] = useState<ConfirmActionProps | false>(false);

  const searchClientsServices = useEffectFn(searchClientsServicesEffect);

  const [{clientsServices, loading}] = useObservable(clientsServicesQuery.clientsServices$);
  const [selectedClientsServices] = useObservable(clientsServicesQuery.selectedClientsServices$);

  const [filters] = useObservable(clientsServicesQuery.filters$);
  const [sortModel] = useObservable(clientsServicesQuery.sort$);
  const [tablePagination] = useObservable(clientsServicesQuery.pagination$);

  useEffect(() => {
    searchClientsServices({filters, tablePagination, sort: sortModel, contractIds: contractIds || []});
  }, [filters, searchClientsServices, sortModel, tablePagination]);

  const handleConfirmValidation = (clientsServiceId: ClientsService) => (handleStopLoading: () => void) => {
    clientsServicesService.deleteClientsServiceInList(clientsServiceId.id)?.pipe(finalize(handleStopLoading)).subscribe({
      next: () => {
        enqueueSnackbar(t('delete.success.client'), {variant: 'success'});
        setOpenConfirmAction(false);
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  const handleEditService = (clientsService: ClientsService, canBeRequested: boolean) => {
    clientsServicesService.updateClientServiceById(clientsService.id, {
      ...clientsServices,
      buyPriceId: clientsService.buyPriceId,
      canBeRequested: canBeRequested
    })?.subscribe({
      next: () => {
        enqueueSnackbar(t('suppliers.details.catalog.manage.success.update'), {variant: 'success'});
      },
      error: (err) => enqueueSnackbar(err.text, err.options),
    });
  };

  return (
    <Stack flex={1} spacing={2} pb="25px" height={showRowsOnly ? 500 : 'auto'} width={showRowsOnly ? '75vw' : 'auto'}>
      {!showRowsOnly && <><Stack direction="row" justifyContent="flex-end" spacing={2}>
          <CheckboxComponent
              label={t('clients.details.contractedServices.manage.selectContract.table.filterExpiredContracts')}
              value={filters.filterExpiredContracts}
              handleChange={clientsServicesService.setFilters('filterExpiredContracts')}
          />
          <CheckboxComponent
              label={t('clients.details.contractedServices.manage.selectContract.table.filterExpiredServices')}
              value={filters.filterExpiredServices}
              handleChange={clientsServicesService.setFilters('filterExpiredServices')}
          />
      </Stack>
          <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
              <Stack direction="row" spacing={1}>
                  <Box width="500px">
                      <SearchComponent
                          value={filters.search}
                          handleChange={(value) => {
                            emitOnce(() => {
                              clientsServicesService.setFilters('search')(value);
                              clientsServicesService.setPagination('page')(1);
                            });
                          }}/>
                  </Box>
                  <SelectComponent
                      inputWidth={300}
                      color="secondary"
                      value={filters.validationStatus}
                      items={ServiceValidationStatusEnum.selectItems()}
                      handleChange={(value) => {
                        clientsServicesService.setFilters('validationStatus')(value === '' ? null : value);
                        clientsServicesService.setPagination('page')(1);
                      }}
                      placeholder={t('invoices.invoicesValidation.filters.status')}
                  />
              </Stack>

              <TableFiltersComponent
                  filters={filters}
                  handleSave={clientsServicesService.setFilters('tableFilters')}
                  handleSaveExclude={clientsServicesService.setFilters('excludeTableFilters')}
                  handleReset={() => clientsServicesService.resetFilters()}
              />
          </Stack> </>}
      <NewTableComponent
        loading={loading}
        columns={[
          COLUMN_TYPES.TEXT_BOLD({field: 'id', label: t('clients.servicesTable.id'), width: 100}),
          COLUMN_TYPES.TEXT_BOLD({
            field: 'service.clientWasteName',
            label: t('clients.servicesTable.name'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.clientWasteName,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.TEXT({
            field: 'schedulesCount',
            label: t('clients.servicesTable.frequency'),
            valueGetter: (params: GridValueGetterParams<any, ClientsService>) => (params.value ?? 0) > 0 ? t('global.frequencies.recurrent') : t('global.frequencies.onDemand'),
            width: 130,
          }),
          COLUMN_TYPES.TEXT({
            field: 'kind.kind',
            label: t('clients.servicesTable.type'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.kindName,
            width: 250,
          }),
          COLUMN_TYPES.TEXT({
            field: 'executionCount',
            label: t('execution.count'),
            valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.executionCount,
            width: 250,
          }),
          COLUMN_TYPES.TEXT({
            field: 'waste.name',
            label: t('clients.servicesTable.details'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.wasteName,
            width: 250,
          }),
          COLUMN_TYPES.TEXT({
            field: 'container.name',
            label: t('clients.servicesTable.container'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.containerName,
            width: 200,
          }),
          COLUMN_TYPES.TEXT({
            field: 'sOrg.name',
            label: t('clients.servicesTable.supplier'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<ClientsService, any>) => params.row.supplierOrganisationUnitName,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.TEXT({
            field: 'cOrgUnit.name',
            label: t('clients.servicesTable.client'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<ClientsService, any>) => params.row.clientOrganisationUnitName,
            fullWidth: true,
            minWidth: 350,
          }),
          COLUMN_TYPES.TEXT({
            field: 'validFromDate',
            label: t('clients.servicesTable.startDate'),
            sortable: true,
            valueGetter: (params: GridValueGetterParams<ClientsService, ClientsService>) => dayjs(params.row.validFromDate).format('DD.MM.YYYY'),
            fullWidth: true,
            minWidth: 100,
          }),
          COLUMN_TYPES.TEXT({
            field: 'buyPriceUnit',
            label: t('clients.servicesTable.purchasePrice'),
            valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.buyPriceIfOnlyOne !== null
              ? `${params.row.buyPriceIfOnlyOne / 100} ${params.row.buyPriceCurrency || ''}`
              : `${params.row.buyPricesCount || '-'}${params.row.buyPricesCount ? ' ' + t('suppliers.details.contractedServices.table.prices') : ''}`,
            width: 130,
          }),
          COLUMN_TYPES.TEXT({
            field: 'sellPriceUnit',
            label: t('clients.servicesTable.sellingPrice'),
            cellClassName: (params: GridCellParams<ClientsService>) => params.row.buyPriceIfOnlyOne && params.row.sellPriceIfOnlyOne && params.row.buyPriceIfOnlyOne > params.row.sellPriceIfOnlyOne ? 'error' : '',
            valueGetter: (params: GridValueGetterParams<any, ClientsService>) => params.row.sellPriceIfOnlyOne !== null
              ? `${params.row.sellPriceIfOnlyOne / 100} ${params.row.sellPriceCurrency || ''}`
              : `${params.row.sellPricesCount || '-'}${params.row.sellPricesCount ? ' ' + t('suppliers.details.contractedServices.table.prices') : ''}`,
            width: 130,
          }),
          ...(enableActions ? [COLUMN_TYPES.CHECKS({
            field: 'canBeRequested',
            label: t('clients.servicesTable.canBeRequested'),
            cellClassName: () => '',
            checks: [
              {
                isChecked: (service: ClientsService) => service.canBeRequested,
                checkedIcon: <BlackCheck/>,
                uncheckedIcon: <GreyCheck/>,
                handleClick: (service: ClientsService, checked: boolean) => {
                  handleEditService(service, checked);
                }
              },
            ],
            width: 150,
          })] : []),
          ...(enableActions ? [COLUMN_TYPES.ACTIONS({
            handleShowRow: (clientsService: ClientsService) => {
              navigate(`${clientsService.id}`);
            },
          })] : []),
        ]}
        getRowClassName={(params: GridRowParams) => {
          if (params.row.expired) {
            return 'grey-row';
          }
          return '';
        }}
        handlePageSizeChange={clientsServicesService.setPagination('limit')}
        handleReload={() => searchClientsServices({
          filters,
          tablePagination,
          sort: sortModel,
          contractIds: contractIds || []
        })}
        pageSize={tablePagination.limit}
        handlePageChange={(page) => clientsServicesService.setPagination('page')(page + 1)}
        page={tablePagination.page - 1}
        handleSortModelChange={clientsServicesService.setSort}
        sortModel={[sortModel]}
        handleRowSelected={clientsServicesService.setSelectedClientsServices}
        rowsSelected={selectedClientsServices}
        disableRowSelection={!enableActions}
        totalRows={clientsServicesQuery.totalRows}
        data={clientsServices}/>
      {!!openConfirmAction && (
        <ConfirmModal
          handleClose={() => setOpenConfirmAction(false)}
          description={t('delete.description.clientUnitService')}
          handleValidate={handleConfirmValidation(openConfirmAction.row)}
          title={t('delete.title')}
          validateButton={OtherActionType.label(openConfirmAction.type)}/>
      )}
      <Outlet/>
    </Stack>
  );
};

export default ClientsServicesTable;
